import { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, UserPlus, LogOut, RefreshCw, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";

export default function TeamPage() {
  const { user } = useAuth();
  const [code, setCode] = useState(null);
  const [members, setMembers] = useState([]);
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, m] = await Promise.all([
      http.get("/team/current-code"), http.get("/team/members"),
    ]);
    setCode(c.data.code);
    setMembers(m.data);
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await http.post("/team/invite-code");
      setCode(r.data.code);
      toast.success("New invite code generated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const copy = () => {
    if (!code) return;
    navigator.clipboard?.writeText(code);
    toast.success("Copied to clipboard");
  };

  const join = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    setBusy(true);
    try {
      await http.post("/team/join", { code: joinCode.trim().toUpperCase() });
      toast.success("Joined team");
      setJoinCode("");
      window.location.reload();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Invalid code");
    } finally { setBusy(false); }
  };

  const leave = async () => {
    if (!window.confirm("Leave this team and return to your own workspace?")) return;
    await http.post("/team/leave");
    toast.success("Left team");
    window.location.reload();
  };

  const isOwner = user && members.length > 0 && members[0]?.team_id === user.id && user.team_id === user.id;

  return (
    <div className="p-4 sm:p-8 md:p-12 max-w-4xl">
      <PageHeader
        eyebrow="Collaboration"
        title="Team"
        description="Invite RBTs to work from the same learners and card library. Everyone on a team sees the same data."
      />

      <div className="grid gap-6 md:grid-cols-2">
        {/* Invite */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Invite code</div>
          {code ? (
            <>
              <div className="flex items-center gap-3">
                <div data-testid="team-invite-code" className="flex-1 text-center font-display font-extrabold text-3xl tracking-[0.25em] py-4 rounded-xl bg-slate-900 text-white">
                  {code}
                </div>
                <button onClick={copy} className="p-3 rounded-xl bg-slate-100 hover:bg-slate-200" title="Copy" data-testid="copy-code">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-3">
                Share this code with your RBTs. Anyone who enters it joins your team.
              </p>
              <Button variant="outline" onClick={generate} disabled={busy} className="rounded-full mt-4"
                data-testid="rotate-code">
                <RefreshCw className="w-3.5 h-3.5 mr-2" /> Generate new code
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-3">No invite code yet.</p>
              <Button onClick={generate} disabled={busy} className="rounded-full" data-testid="generate-code">
                <UserPlus className="w-4 h-4 mr-2" /> Create invite code
              </Button>
            </>
          )}
        </div>

        {/* Join */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Join a team</div>
          <form onSubmit={join} className="flex gap-2">
            <Input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="8-character code"
              className="rounded-xl uppercase tracking-widest font-bold"
              data-testid="join-code-input"
              maxLength={12}
            />
            <Button type="submit" disabled={busy || !joinCode.trim()} className="rounded-full" data-testid="join-team-button">
              Join
            </Button>
          </form>
          <p className="text-xs text-slate-500 mt-3">
            Joining a team switches your workspace to the inviting BCBA's — you'll see their learners and cards instead of your own.
          </p>
          {user && user.team_id && user.team_id !== user.id && (
            <Button variant="outline" onClick={leave} className="rounded-full mt-4 text-rose-600 border-rose-200 hover:bg-rose-50"
              data-testid="leave-team-button">
              <LogOut className="w-3.5 h-3.5 mr-2" /> Leave current team
            </Button>
          )}
        </div>
      </div>

      {/* Members */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <UsersRound className="w-4 h-4 text-slate-500" />
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
            Team members ({members.length})
          </div>
        </div>
        <ul className="divide-y divide-slate-100">
          {members.map((m) => (
            <li key={m.id} className="py-3 flex items-center justify-between" data-testid={`team-member-${m.id}`}>
              <div>
                <div className="font-semibold text-slate-900">{m.name}</div>
                <div className="text-xs text-slate-500">{m.email}</div>
              </div>
              {m.id === user?.id && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-900 text-white">You</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
